import { TeamDetailResponse, TeamStatus } from "@/web-client/api";
import { FC, useMemo } from "react";
import Avatar, { AvatarSize } from "@/components/Avatar";
import useBtnRequiresIsDesigner from "@/features/user/hooks/useBtnRequiresDesigner";
import { useRouter } from "next/router";
import useIsMyTeam from "@/features/team/hooks/useIsMyTeam";
import Maybe from "@/components/Maybe";
import UnauthorizedTeamChip from "@/features/team/components/UnauthorizedTeamChip";
import MenuNavigation from "@/components/MenuNavigation";
import Button from "@/components/Button";
import useDisclosure from "@/hooks/useDisclosure";
import { useRequestJoinTeam } from "../hooks/useRequestJoinTeam";
import useUserStatus from "@/features/user/hooks/useUserStatus";
import IconButton from "@/components/IconButton";
import IconX from "@/assets/imgs/svg/icon_x.svg";
import IconHomeFill from "@/assets/imgs/svg/icon-home-fill.svg";
import IconInstagramFill from "@/assets/imgs/svg/icon-instagram-fill.svg";
import IconFacebookFill from "@/assets/imgs/svg/icon-facebook-fill.svg";
import IconSettings from "@/assets/imgs/svg/icon-settings.svg";
import Link from "next/link";
import Image from "next/image";

interface Props {
  team: TeamDetailResponse;
}

const TeamHeader: FC<Props> = ({ team }): JSX.Element => {
  const router = useRouter();
  const isOwner = useIsMyTeam(team.id, {
    isOwner: true,
    skipCheckDesigner: true,
  });
  const isMyTeam = useIsMyTeam(team.id, { skipCheckDesigner: true });
  const { isDesigner } = useUserStatus();
  const { clickHandler: clickEditBtnHandler } = useBtnRequiresIsDesigner(() => {
    router.push(`/teams/${team.id}/edit/profile`);
  });
  const menuDisclosure = useDisclosure();
  const { call, Dialog } = useRequestJoinTeam(team.id, () => {
    menuDisclosure.closeHandler();
  });

  const hasSocialAccount = useMemo(() => {
    if (!team.links) return false;

    return (
      !!team.links.homepage ||
      !!team.links.instagram ||
      !!team.links.twitter ||
      !!team.links.facebook
    );
  }, [team]);

  return (
    <header className="relative w-full bg-white shadow-default p-24 rounded-xs">
      <div className="absolute top-[24px] right-[24px] z-50">
        <Maybe condition={isOwner}>
          <IconButton
            variant="secondary"
            size="large"
            onClick={clickEditBtnHandler}
            aria-label="チームの編集"
          >
            <IconSettings />
          </IconButton>
        </Maybe>

        <Maybe condition={!isMyTeam && isDesigner}>
          <MenuNavigation {...menuDisclosure}>
            <Button color="ghost" size="small" onClick={call}>
              このチームに参加申請を送る
            </Button>
          </MenuNavigation>

          <Dialog />
        </Maybe>
      </div>

      <div className="grid gap-16">
        <div className="grid gap-32">
          <div className="grid gap-12 justify-items-center">
            <Avatar
              src={
                team.logo ? team.logo.urls.small : "/imgs/default_team_icon.png"
              }
              size={AvatarSize.Huge}
              alt={`${team.name}のロゴ`}
            />
            <h1 className="font-bold text-xl break-all">{team.name}</h1>
            <Maybe condition={team.status === TeamStatus.PENDING}>
              <UnauthorizedTeamChip />
            </Maybe>
          </div>

          {team.profile && (
            <p className="text-xs leading-loose whitespace-pre-wrap">
              {team.profile}
            </p>
          )}
        </div>

        {team.award_results && team.award_results.length > 0 && (
          <dl className="grid gap-8">
            <dt className="font-bold text-xs">AWARD</dt>
            <dd>
              <ul className="grid gap-8">
                {team.award_results.map(
                  ({ id, prize_name, award: { slug } }) => (
                    <li key={id + slug}>
                      <div className="flex items-center gap-8">
                        <Image
                          src={`/imgs/awards/${slug}/badge.png`}
                          width={20}
                          height={24}
                          alt={prize_name}
                        />
                        <span className="text-xs">{prize_name}</span>
                      </div>
                    </li>
                  ),
                )}
              </ul>
            </dd>
          </dl>
        )}

        {hasSocialAccount && (
          <>
            <hr />

            <ul className="flex items-center justify-center gap-16">
              {team.links.homepage && (
                <li>
                  <IconButton
                    variant="secondary"
                    aria-label={`${team.name}のホームページ`}
                    asChild
                  >
                    <Link target="_blank" href={team.links.homepage}>
                      <IconHomeFill width={16} height={16} />
                    </Link>
                  </IconButton>
                </li>
              )}

              {team.links.instagram && (
                <li>
                  <IconButton
                    variant="secondary"
                    aria-label={`${team.name}のインスタグラム`}
                    asChild
                  >
                    <Link target="_blank" href={team.links.instagram}>
                      <IconInstagramFill width={16} height={16} />
                    </Link>
                  </IconButton>
                </li>
              )}

              {team.links.twitter && (
                <li>
                  <IconButton
                    variant="secondary"
                    aria-label={`${team.name}のX`}
                    asChild
                  >
                    <Link target="_blank" href={team.links.twitter}>
                      <IconX width={16} height={16} />
                    </Link>
                  </IconButton>
                </li>
              )}

              {team.links.facebook && (
                <li>
                  <IconButton
                    variant="secondary"
                    aria-label={`${team.name}のFacebook`}
                    asChild
                  >
                    <Link target="_blank" href={team.links.facebook}>
                      <IconFacebookFill width={16} height={16} />
                    </Link>
                  </IconButton>
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </header>
  );
};
export default TeamHeader;
